import React, { useId } from 'react';
import Input from './Input.jsx';
import './FormField.css';

/**
 * FormField component with ArbiWin design system integration
 * Combines Input with consistent form field styling and validation
 * Based on the registration form design pattern
 *
 * @param {Object} props - FormField properties
 * @param {string} props.type - Input type: 'text', 'email', 'password', 'number', 'tel', 'url'
 * @param {string} props.name - Field name for form submission
 * @param {string} props.label - Field label text
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.value - Input value (controlled)
 * @param {string} props.defaultValue - Default input value (uncontrolled)
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onBlur - Blur handler
 * @param {Function} props.onFocus - Focus handler
 * @param {boolean} props.required - Mark field as required
 * @param {boolean} props.disabled - Disable field
 * @param {boolean} props.readonly - Make field readonly
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {string} props.autocomplete - Autocomplete attribute
 * @param {string} props.size - Field size: 'small', 'medium', 'large'
 * @param {boolean} props.fullWidth - Make field full width
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @param {React.ReactNode} props.startIcon - Icon at start of input
 * @param {React.ReactNode} props.endIcon - Icon at end of input
 * @returns {JSX.Element} FormField component
 */
const FormField = function FormField({
  type = 'text',
  name,
  label,
  placeholder,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  required = false,
  disabled = false,
  readonly = false,
  error,
  helperText,
  autocomplete,
  size = 'large', // Default to large for form fields (matches registration design)
  fullWidth = true, // Default to full width for form fields
  className = '',
  testId,
  startIcon,
  endIcon,
  ...rest
}) {

  const fieldId = useId();
  const hasError = Boolean(error);

  const fieldClasses = [
    'arbiwin-form-field',
    'arbiwin-form-field--' + size,
    hasError ? 'arbiwin-form-field--error' : '',
    disabled ? 'arbiwin-form-field--disabled' : '',
    fullWidth ? 'arbiwin-form-field--full-width' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={fieldClasses}>
      <Input
        id={fieldId}
        type={type}
        name={name}
        label={label}
        placeholder={placeholder}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        required={required}
        disabled={disabled}
        readonly={readonly}
        error={error}
        helperText={helperText}
        size={size}
        fullWidth={fullWidth}
        startIcon={startIcon}
        endIcon={endIcon}
        testId={testId || 'arbiwin-form-field'}
        autoComplete={autocomplete}
        {...rest}
      />
    </div>
  );
};

/**
 * FormGroup component for grouping related form fields
 *
 * @param {Object} props - FormGroup properties
 * @param {React.ReactNode} props.children - Form fields to group
 * @param {string} props.title - Group title
 * @param {string} props.description - Group description
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} FormGroup component
 */
const FormGroup = function FormGroup({
  children,
  title,
  description,
  className = '',
  testId
}) {

  const groupClasses = [
    'arbiwin-form-group',
    className
  ].filter(Boolean).join(' ');

  return (
    <fieldset
      className={groupClasses}
      data-testid={testId || 'arbiwin-form-group'}
    >
      {title && (
        <legend className="arbiwin-form-group__title">
          {title}
        </legend>
      )}
      {description && (
        <p className="arbiwin-form-group__description">
          {description}
        </p>
      )}
      <div className="arbiwin-form-group__content">
        {children}
      </div>
    </fieldset>
  );
};

/**
 * FormRow component for horizontal field layouts
 *
 * @param {Object} props - FormRow properties
 * @param {React.ReactNode} props.children - Form fields in the row
 * @param {string} props.gap - Gap between fields: 'small', 'medium', 'large'
 * @param {string} props.align - Vertical alignment: 'start', 'center', 'end'
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} FormRow component
 */
const FormRow = function FormRow({
  children,
  gap = 'medium',
  align = 'start',
  className = '',
  testId
}) {

  const rowClasses = [
    'arbiwin-form-row',
    'arbiwin-form-row--gap-' + gap,
    'arbiwin-form-row--align-' + align,
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={rowClasses}
      data-testid={testId || 'arbiwin-form-row'}
    >
      {children}
    </div>
  );
};

FormField.displayName = 'FormField';
FormGroup.displayName = 'FormGroup';
FormRow.displayName = 'FormRow';

export default FormField;
export { FormGroup, FormRow };
